import { useState, type FormEvent } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import Button from "@web-ui/components/Button";
import Field from "@web-ui/components/Field";
import Notice from "@web-ui/components/Notice";
import { session, type Staff } from "@/api/admin";
import type { AdminClient } from "@/api/client";
import { emailProblem, failureMessage } from "./messages";
import { PasskeyButton } from "./passkey-button";

/** The first step: the work email, then a mailed code. A passkey skips the code. */
export function EmailStep({ client, initialEmail, onCodeSent, onSignedIn }: { client: AdminClient; initialEmail: string; onCodeSent(email: string): void; onSignedIn(staff: Staff): void }) {
  const [email, setEmail] = useState(initialEmail);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = emailProblem(email);
    setFieldError(problem ?? undefined);
    if (problem) return;
    setBusy(true); setError(null);
    try { await session.emailStart(client, email.trim()); onCodeSent(email.trim()); }
    catch (failure) { setError(failureMessage(failure)); } finally { setBusy(false); }
  };

  const passkey = async () => {
    const { challengeId, options } = await session.passkeyOptions(client);
    const response = await startAuthentication({ optionsJSON: options as Parameters<typeof startAuthentication>[0]["optionsJSON"] });
    onSignedIn((await session.passkeyComplete(client, challengeId, response)).staff);
  };

  return (
    <form onSubmit={submit} noValidate>
      {error ? <Notice tone="critical" className="mb-6">{error}</Notice> : null}
      <Field innerLabel="Work email" placeholder="Enter email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} onBlur={() => { if (email.trim()) setFieldError(emailProblem(email) ?? undefined); }} error={fieldError} required />
      <Button className="mt-6 w-full" type="submit" variant="primary" isPending={busy} disabled={busy}>Email me a code</Button>
      <PasskeyButton className="mt-3 w-full" label="Sign in with a passkey" run={passkey} onError={setError} />
      <div className="mt-4 text-center text-body-2 text-t-secondary">
        Not on the team yet?&nbsp;Ask an owner of {client.brand.name} to add you.
      </div>
    </form>
  );
}
